// stytch.store.ts
import { defineStore } from 'pinia'
import { ref, type Ref, watch } from 'vue'
import { AuthFlowType, B2BProducts, B2BOAuthProviders } from '@stytch/vanilla-js/b2b'
import { StytchB2BUIClient } from '@stytch/vanilla-js/b2b'
import router from '@/router'

export const callbacks = {
    onEvent: ({ type, data }: any) => {
        switch (type) {
            case 'AUTHENTICATE_FLOW_COMPLETE':
                console.log('AUTHENTICATE_FLOW_COMPLETE')
                router.push('/')
                break
            default:
                console.log('stytch onEvent default', type, data)
        }
    },
    onError: (data: any) => {
        console.log('stytch onError', data)
    },
}

export const styles = {
    fontFamily: 'Arial',
    logo: {
        logoImageUrl: '',
    },
    container: {
        width: '100%',
        backgroundColor: '#ffffff',
        borderColor: '#e0e0e0',
        borderRadius: '0px',
    },
    colors: {
        primary: '#000000',
        secondary: '#666666',
        success: '#4ade80',
        error: '#f87171',
    },
    buttons: {
        primary: {
            backgroundColor: '#000000',
            textColor: '#ffffff',
            borderColor: '#0f0f0f',
            borderRadius: '0px',
        },
        secondary: {
            backgroundColor: '#ffffff',
            textColor: '#000000',
            borderColor: '#e0e0e0',
            borderRadius: '0px',
        },
    },
    inputs: {
        borderColor: '#e0e0e0',
        borderRadius: '0px',
        textColor: '#000000',
        placeholderColor: '#666666',
        backgroundColor: '#ffffff',
    },
}

export const discoveryConfig = {
    authFlowType: AuthFlowType.Discovery,
    products: [B2BProducts.sso, B2BProducts.oauth, B2BProducts.emailMagicLinks, B2BProducts.passwords],
    sessionOptions: { sessionDurationMinutes: 60 },
    emailMagicLinksOptions: {
        loginRedirectURL: window.location.origin + '/auth/signin',
        signupRedirectURL: window.location.origin + '/auth/singup',
        discoveryRedirectURL: window.location.origin + '/auth/signin',
    },
    passwordOptions: {
        loginExpirationMinutes: 30,
        loginRedirectURL: window.location.origin + '/auth/singin  ',
        resetPasswordRedirectURL: window.location.origin + '/auth/recover',
        discoveryRedirectURL: window.location.origin + '/auth/signin',
    },
    oauthOptions: {
        providers: [{ type: B2BOAuthProviders.Google }],
        loginRedirectURL: window.location.origin + '/auth/signin',
        signupRedirectURL: window.location.origin + '/auth/signup',
        discoveryRedirectURL: window.location.origin + '/auth/signin',
    },
    ssoOptions: {
        loginRedirectURL: window.location.origin + '/auth/signin',
        signupRedirectURL: window.location.origin + '/auth/signup',
    },
}

const STYTCH_PUBLIC_TOKEN = import.meta.env.VITE_STYTCH_PUBLIC_TOKEN || ''
const initialStytch = new StytchB2BUIClient(STYTCH_PUBLIC_TOKEN, {})

export const useStytchStore = defineStore(
    'stytch',
    () => {
        // State
        const stytch: Ref<any> = ref(initialStytch)
        const user: Ref<null | Record<string, any>> = ref(null)
        // Keep the raw session object (Stytch may return different shapes)
        const session: Ref<null | Record<string, any>> = ref(null)
        // Keep any session JWT/token returned by the SDK
        const jwtToken: Ref<null | string> = ref(null)
        // Keep raw token set returned by session.getTokens() (access/refresh/id tokens etc)
        const tokens: Ref<null | Record<string, any>> = ref(null)
    // Top-level roles extracted from session or user
    const roles: Ref<string[]> = ref([])
    const organizations: Ref<any[]> = ref([])
        const loading: Ref<boolean> = ref(false)
        const error: Ref<null | string> = ref(null)
        const isAuthenticated: Ref<boolean> = ref(false)

        // Keep isAuthenticated in sync with user
        watch(
            user,
            (u) => {
                isAuthenticated.value = !!u
            },
            { immediate: true }
        )

        const deriveOrganizationKey = (org: any) => {
            const raw = (org && org.organization) || org || {}
            const keyCandidates = [
                raw.organization_id,
                raw.id,
                raw.slug,
                raw.organization_slug,
                raw.organization?.organization_id,
                raw.organization?.id,
                raw.organization?.slug,
                raw.organization_name,
                raw.name,
            ].filter((value) => typeof value === 'string' && value.length > 0)

            if (keyCandidates.length > 0) {
                return keyCandidates[0]
            }

            try {
                return JSON.stringify(raw)
            } catch {
                return String(raw)
            }
        }

        const collectOrganizations = (sessionCandidate: any, userCandidate: any) => {
            const collected: any[] = []

            const pushValue = (value: any) => {
                if (!value) return
                if (Array.isArray(value)) {
                    value.forEach((item) => pushValue(item?.organization || item))
                    return
                }
                collected.push((value && value.organization) || value)
            }

            if (sessionCandidate) {
                pushValue(sessionCandidate.organizations)
                pushValue(sessionCandidate.organization)

                if (
                    !sessionCandidate.organization &&
                    (sessionCandidate.organization_id || sessionCandidate.organization_slug || sessionCandidate.organization_name)
                ) {
                    collected.push({
                        organization_id: sessionCandidate.organization_id,
                        organization_slug: sessionCandidate.organization_slug,
                        organization_name: sessionCandidate.organization_name,
                    })
                }
            }

            if (userCandidate) {
                pushValue(userCandidate.organizations)
                pushValue(userCandidate.organization)

                if (
                    !userCandidate.organization &&
                    (userCandidate.organization_id || userCandidate.organization_slug || userCandidate.organization_name)
                ) {
                    collected.push({
                        organization_id: userCandidate.organization_id,
                        organization_slug: userCandidate.organization_slug,
                        organization_name: userCandidate.organization_name || userCandidate.organization_slug,
                    })
                }
            }

            return collected
        }

        const updateOrganizations = (sessionCandidate: any, userCandidate: any) => {
            const candidates = collectOrganizations(sessionCandidate, userCandidate)

            if (!sessionCandidate && !userCandidate) {
                organizations.value = []
                return
            }

            if (candidates.length === 0) {
                organizations.value = []
                return
            }

            const seen = new Set<string>()
            const deduped: any[] = []

            candidates.forEach((candidate) => {
                if (!candidate) return
                const key = deriveOrganizationKey(candidate)
                if (key && seen.has(key)) return
                if (key) seen.add(key)
                deduped.push(candidate)
            })

            organizations.value = deduped
        }

        // Best effort session sync when client changes
        watch(
            stytch,
            async (client: any) => {
                if (!client?.session) return
                try {
                    let sess: any = null
                    if (typeof client.session.getSync === 'function') {
                        sess = await client.session.getSync()
                    } else if (typeof client.session.get === 'function') {
                        sess = await client.session.get()
                    }
                    // If the SDK exposes getTokens, prefer fetching the token set
                    try {
                        if (typeof client.session.getTokens === 'function') {
                            const tok = await client.session.getTokens()
                            tokens.value = tok || null
                        }
                    } catch {
                        /* ignore token fetch errors */
                    }
                    // store the raw session
                    session.value = sess || null
                    // best-effort extract common JWT/token fields: prefer token set if available
                    jwtToken.value =
                        tokens.value?.access_token ||
                        tokens.value?.id_token ||
                        tokens.value?.token ||
                        sess?.session_jwt ||
                        sess?.jwt ||
                        sess?.session_token ||
                        sess?.token ||
                        null

                    // normalize user/member and roles for the app
                    if (sess && (sess.member || sess.user)) {
                        user.value = sess.member || sess.user
                        roles.value = (user.value && user.value.roles) || sess.roles || []
                        isAuthenticated.value = true
                        updateOrganizations(sess, user.value)
                    } else {
                        // Some Stytch responses provide a top-level session object
                        // that is not nested under `member`/`user` but contains
                        // member identifiers and roles (see sample payload).
                        if (sess && (sess.member_id || sess.member_session_id || sess.organization_id || sess.roles)) {
                            // create a lightweight user-like object from the top-level session
                            user.value = {
                                member_id: sess.member_id,
                                member_session_id: sess.member_session_id,
                                organization_id: sess.organization_id,
                                organization_slug: sess.organization_slug,
                                roles: sess.roles || [],
                                authentication_factors: sess.authentication_factors || [],
                                started_at: sess.started_at,
                                expires_at: sess.expires_at,
                            }
                            roles.value = sess.roles || []
                            isAuthenticated.value = true
                            updateOrganizations(sess, user.value)
                        } else {
                            user.value = null
                            roles.value = []
                            isAuthenticated.value = false
                            updateOrganizations(sess, null)
                        }
                    }
                } catch {
                    /* ignore */
                }
            },
            { immediate: true }
        )

        // ----- helpers -----
        const syncSession = async () => {
            try {
                const client: any = stytch.value
                if (!client?.session) return { success: false, error: 'No session API' }

                let sess: any = null
                if (typeof client.session.getSync === 'function') {
                    sess = await client.session.getSync()
                } else if (typeof client.session.get === 'function') {
                    sess = await client.session.get()
                }
                // Attempt to fetch token set if SDK exposes it
                try {
                    if (typeof client.session.getTokens === 'function') {
                        const tok = await client.session.getTokens()
                        tokens.value = tok || null
                    }
                } catch {
                    /* ignore */
                }
                // store raw session and normalize user/roles
                session.value = sess || null
                jwtToken.value =
                    tokens.value?.access_token ||
                    tokens.value?.id_token ||
                    tokens.value?.token ||
                    sess?.session_jwt ||
                    sess?.jwt ||
                    sess?.session_token ||
                    sess?.token ||
                    null
                if (sess && (sess.member || sess.user)) {
                    user.value = sess.member || sess.user
                    roles.value = (user.value && user.value.roles) || sess.roles || []
                    isAuthenticated.value = true
                    updateOrganizations(sess, user.value)
                } else if (sess && (sess.member_id || sess.member_session_id || sess.organization_id || sess.roles)) {
                    user.value = {
                        member_id: sess.member_id,
                        member_session_id: sess.member_session_id,
                        organization_id: sess.organization_id,
                        organization_slug: sess.organization_slug,
                        roles: sess.roles || [],
                        authentication_factors: sess.authentication_factors || [],
                        started_at: sess.started_at,
                        expires_at: sess.expires_at,
                    }
                    roles.value = sess.roles || []
                    isAuthenticated.value = true
                    updateOrganizations(sess, user.value)
                } else {
                    user.value = null
                    roles.value = []
                    isAuthenticated.value = false
                    updateOrganizations(null, null)
                }
                return { success: true, session: sess }
            } catch (e: any) {
                error.value = e?.message || String(e)
                return { success: false, error: error.value }
            }
        }

        // Ingest a known session object (useful when you already have the payload)
        const ingestSession = (sess: Record<string, any> | null) => {
            session.value = sess || null
            // clear any previously stored token set when ingesting a null session
            if (!sess) tokens.value = null
            jwtToken.value = sess?.session_jwt || sess?.jwt || sess?.session_token || sess?.token || null
            if (!sess) {
                user.value = null
                roles.value = []
                isAuthenticated.value = false
                updateOrganizations(null, null)
                return
            }
            if (sess.member || sess.user) {
                user.value = sess.member || sess.user
                roles.value = (user.value && user.value.roles) || sess.roles || []
                isAuthenticated.value = true
                updateOrganizations(sess, user.value)
                return
            }
            if (sess.member_id || sess.member_session_id || sess.organization_id || sess.roles) {
                user.value = {
                    member_id: sess.member_id,
                    member_session_id: sess.member_session_id,
                    organization_id: sess.organization_id,
                    organization_slug: sess.organization_slug,
                    roles: sess.roles || [],
                    authentication_factors: sess.authentication_factors || [],
                    started_at: sess.started_at,
                    expires_at: sess.expires_at,
                }
                roles.value = sess.roles || []
                isAuthenticated.value = true
                updateOrganizations(sess, user.value)
                return
            }
            // fallback
            user.value = null
            roles.value = []
            isAuthenticated.value = false
            updateOrganizations(null, null)
        }

        // Call this once at app startup
        const initAuthWatcher = () => {
            // 1) Immediate startup sync (covers hard reload)
            syncSession()

            // 2) Focus sync (covers returning to a tab after SSO on another)
            const onFocus = () => {
                syncSession()
            }
            const onVisibilityChange = () => {
                if (document.visibilityState === 'visible') onFocus()
            }
            window.addEventListener('visibilitychange', onVisibilityChange)
            window.addEventListener('focus', onFocus)

            // 3) Storage sync (multi tab scenarios; we write a flag below).
            // Also try to detect other Stytch-related keys if the SDK writes into storage.
            const onStorage = (e: StorageEvent) => {
                try {
                    if (!e.key) return
                    // catch our explicit ping and any other keys the SDK may use like 'stytch:session'
                    if (e.key === 'stytch:session:ping' || e.key === 'stytch:session' || e.key.startsWith('stytch')) {
                        syncSession()
                    }
                } catch {
                    /* ignore */
                }
            }
            window.addEventListener('storage', onStorage)

            // 4) Heartbeat to refresh local state. Use a shorter interval so UI updates sooner
            // when the SDK updates session in another tab/window. Keep a reasonably-sized interval
            // to avoid excessive network calls.
            const interval = setInterval(() => {
                syncSession()
            }, 1000)

            // 5) Session refresh every 50 minutes to keep the session alive
            const authenticateSession = async () => {
                try {
                    const client: any = stytch.value
                    if (!client?.session) return
                    
                    // Check if session exists before attempting to refresh
                    let currentSession: any = null
                    if (typeof client.session.getSync === 'function') {
                        currentSession = await client.session.getSync()
                    } else if (typeof client.session.get === 'function') {
                        currentSession = await client.session.get()
                    }
                    
                    if (currentSession && typeof client.session.authenticate === 'function') {
                        await client.session.authenticate({
                            session_duration_minutes: 60,
                        })
                        // Sync local state after refresh
                        await syncSession()
                        pingOtherTabs()
                    }
                } catch (error) {
                    console.warn('[Stytch] Session refresh failed:', error)
                }
            }
            
            // Refresh session every 50 minutes (3000000 ms)
            const refreshInterval = setInterval(authenticateSession, 3000000)

            // Basic teardown helper you can call on app unmount if needed
            const teardown = () => {
                clearInterval(interval)
                clearInterval(refreshInterval)
                window.removeEventListener('focus', onFocus)
                window.removeEventListener('visibilitychange', onVisibilityChange)
                window.removeEventListener('storage', onStorage)
            }
            
            // Clear intervals on page unload
            window.addEventListener('beforeunload', () => {
                clearInterval(interval)
                clearInterval(refreshInterval)
            })
            
            return teardown
        }

        // Call this after any auth state change so other tabs update too
        const pingOtherTabs = () => {
            try {
                localStorage.setItem('stytch:session:ping', String(Date.now()))
            } catch {
                /* ignore */
            }
        }

        // ----- actions you already had (trimmed to show where to ping) -----
        const logout = () => {
            try {
                stytch.value.session.revoke()
            } catch {
                /* ignore */
            }
            user.value = null
            session.value = null
            jwtToken.value = null
            roles.value = []
            isAuthenticated.value = false
            organizations.value = []
            pingOtherTabs()

            router.push('/auth/signin')
        }

        const authenticate = async (token: string) => {
            loading.value = true
            error.value = null
            try {
                const resp = await stytch.value.magicLinks.authenticate({
                    magic_links_token: token,
                    session_duration_minutes: 60,
                })
                user.value = resp.member || resp.user || null
                // Some SDK responses include JWT/session info directly on the authenticate response.
                // Capture it immediately so callers can access the token before a subsequent sync.
                jwtToken.value = resp?.session_jwt || resp?.jwt || resp?.session_token || resp?.token || null
                if (resp && (resp.session_jwt || resp.session_token || resp.member_session || resp.member_session_id)) {
                    // store the raw response as session when it looks session-like
                    session.value = resp
                    updateOrganizations(resp, user.value)
                }
                isAuthenticated.value = !!user.value
                // refresh local session from the client in case SDK has more session data
                try {
                    await syncSession()
                } catch {
                    /* ignore */
                }
                // notify other tabs so they can sync sooner
                pingOtherTabs()
            } catch (err: any) {
                const code = err?.error_type || err?.status_code || ''
                if (
                    err?.message?.includes('magic_link_not_found') ||
                    code === 'magic_link_not_found' ||
                    err?.status_code === 404
                ) {
                    error.value = 'This magic link is invalid, expired, or already used. Please request a new one.'
                } else if (err?.message?.includes('expired')) {
                    error.value = 'Magic link expired. Request a new link.'
                } else {
                    error.value = err?.message || 'Failed to authenticate'
                }
                isAuthenticated.value = false
            } finally {
                loading.value = false
            }
        }
        // Login / Discovery UI
        const mountDiscoveryUI = async (
            elementId = 'stytch-discovery',
            config = discoveryConfig,
            clbs: {
                onSuccess?: () => void
                onError?: (error: string) => void
            } = callbacks
        ) => {
            const id = elementId.startsWith('#') ? elementId.slice(1) : elementId

            // resolve element with a microtask retry
            let el = document.getElementById(id)
            if (!el) {
                await new Promise((r) => setTimeout(r, 0))
                el = document.getElementById(id)
            }
            if (!el) {
                console.error(`[Stytch] mountDiscoveryUI: element "${id}" not found`)
                return { success: false, error: `Element "${id}" not found` }
            }

            try {
                // Attempt mount with plain id, then with #id as some SDK builds expect the hash
                const tryMount = async (selector: string) => {
                    try {
                        await stytch.value.mount({
                            elementId: selector,
                            config,
                            callbacks: clbs,
                            styles,
                        })
                        return true
                    } catch (e: any) {
                        console.warn('[Stytch] mount discovery failed for', selector, e?.message || e)
                        return false
                    }
                }

                if (!(await tryMount(id))) {
                    if (!(await tryMount('#' + id))) {
                        return { success: false, error: 'Stytch discovery mount failed' }
                    }
                }

                // Best effort session sync after UI is live
                await syncSession()
                return { success: true }
            } catch (e: any) {
                return { success: false, error: e?.message || 'Failed to mount discovery UI' }
            }
        }
        // Reset Password UI
        const mountResetPasswordUI = async (elementId = '#stytch-sdk-reset', passwordResetToken?: string) => {
            loading.value = true
            error.value = null
            try {
                // token from arg or URL
                const token = passwordResetToken || new URLSearchParams(window.location.search).get('token')

                if (!token) {
                    error.value = 'Missing password reset token in URL'
                    return { success: false, error: error.value }
                }

                const id = elementId.startsWith('#') ? elementId : `#${elementId}`
                let el = document.querySelector(id)
                if (!el) {
                    await new Promise((r) => setTimeout(r, 0))
                    el = document.querySelector(id)
                }
                if (!el) {
                    error.value = `Cannot mount reset password UI: element '${id}' not found`
                    return { success: false, error: error.value }
                }

                // Minimal reset config
                const resetConfig: any = {
                    passwordOptions: {
                        loginExpirationMinutes: 30,
                        loginRedirectURL: window.location.origin + '/auth/signup',
                        resetPasswordExpirationMinutes: 30,
                        resetPasswordRedirectURL: window.location.origin + '/auth/recover',
                    },
                    products: [(globalThis as any).Products?.passwords].filter(Boolean),
                }

                const client: any = stytch.value
                if (typeof client.mountResetPassword !== 'function') {
                    throw new Error('mountResetPassword is not available on this Stytch client')
                }

                await client.mountResetPassword({
                    elementId: id,
                    config: resetConfig,
                    passwordResetToken: token,
                })

                // Try to update local session info right after mount
                await syncSession()

                // Clean token from URL so it is not kept in history
                try {
                    const qs = new URLSearchParams(window.location.search)
                    if (qs.get('token')) {
                        const clean = new URL(window.location.href)
                        clean.searchParams.delete('token')
                        window.history.replaceState({}, '', clean.toString())
                    }
                } catch {}

                return { success: true }
            } catch (e: any) {
                error.value = e?.message || 'Failed to mount reset password UI'
                return { success: false, error: error.value }
            } finally {
                loading.value = false
            }
        }

        // Expose everything you already return, plus initAuthWatcher and pingOtherTabs
        return {
            // state
            stytch,
            user,
            session,
            jwtToken,
            tokens,
            roles,
            loading,
            error,
            isAuthenticated,
            // actions
            syncSession,
            initAuthWatcher,
            pingOtherTabs,
            ingestSession,
            authenticate,
            logout,
            mountDiscoveryUI,
            mountResetPasswordUI,
            organizations,
            // keep your other actions here: mountDiscoveryUI, mountResetPasswordUI, resetPassword, etc.
        }
    },
    { persist: false }
)
